import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import type {ReferralJob} from 'src/types/referral-v2';
import {useDispatch, useSelector} from 'react-redux';
import {isSubstringPresent} from 'src/components/referral-v2/common';
import {updatedJobsListing} from 'src/action-creators/referral-external';
import {selectFormData, selectJobs} from 'src/selectors/referral-external';
import {selectUserAgent} from 'src/selectors/accounts';
import {isMobile} from 'src/utils/responsive';
import {useAsyncDependency} from 'src/hooks/useAsyncDependency';
import {SearchInput} from '@spaced-out/ui-design-system/lib/components/SearchInput';
import {ButtonDropdown} from '@spaced-out/ui-design-system/lib/components/ButtonDropdown';
import {CircularLoader} from '@spaced-out/ui-design-system/lib/components/CircularLoader';
import {EmptyState} from '@spaced-out/ui-design-system/lib/components/EmptyState';
import {type MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';
import {
  JobCard,
  MobileViewJobCard,
} from 'src/components/referral-external/jobs/job-card';
import css from './jobs.css';


const JobListing = ({
  selectJob,
  setJobDetailsOpen,
  handleSelectJob,
  referralLinkId,
  agentId,
}: {
  selectJob: (arg1: string) => void;
  setJobDetailsOpen: (arg1: boolean) => void;
  handleSelectJob: (arg1: ReferralJob) => void;
  referralLinkId: string;
  agentId?: string;
}) => {
  const {t} = useLabelTranslation();
  const [searchText, setSearchText] = React.useState('');
  const [selectedLocation, setSelectedLocation] = React.useState('');
  const dispatch = useDispatch();
  const jobs = useSelector(selectJobs) || [];
  const {job_list_enabled} = useSelector(selectFormData);

  const userAgent = useSelector(selectUserAgent);

  const jobsLoading = useAsyncDependency(() => {
    return dispatch(
      updatedJobsListing(
        referralLinkId,
        agentId,
        {
          search_str: searchText,
          location: [selectedLocation],
        },
        job_list_enabled,
      ),
    );
  }, []);

  const filteredJob = React.useMemo(() => {
    const isLocationMatched = (job: any) => {
      return selectedLocation ? selectedLocation === job.location : true;
    };

    return jobs.filter(
      (job) =>
        job.id &&
        (!searchText || isSubstringPresent(searchText, job.title)) &&
        isLocationMatched(job),
    );
  }, [searchText, selectedLocation, jobs]);

  const handleChangeLocation = (location: MenuOption) => {
    const updatedLocation =
      selectedLocation === location.key ? '' : location.key;
    setSelectedLocation(updatedLocation);
  };

  const getLocationOptions = (): MenuOption[] => {
    const uniqueLocations = new Set();
    jobs?.forEach((job: any) => {
      if (job.location) {
        uniqueLocations.add(job.location);
      }
    });
    const options = Array.from(uniqueLocations).map((location) => ({
      key: location,
      label: location,
    }));
    // @ts-expect-error - TS2322 - Type '{ key: unknown; label: unknown; }[]' is not assignable to type 'MenuOption[]'.
    return options;
  };

  return (
    <div className={css.jobsContainer}>
      <div className={css.navBar}>
        <SearchInput
          placeholder={t('Search_by_title', 'Search by title')}
          onChange={(e) => setSearchText(e.target.value)}
          size="medium"
          value={searchText}
          onClear={() => setSearchText('')}
          classNames={{wrapper: css.searchInput}}
        />
        <ButtonDropdown
          isLoading={jobsLoading}
          classNames={{buttonWrapper: css.dropdownMenu}}
          menu={{
            allowSearch: true,
            isFluid: false,
            options: getLocationOptions(),
            selectedKeys: [selectedLocation],
            staticLabels: {
              SEARCH_PLACEHOLDER: `${t('Search', 'Search')}...`,
              RESULT: t('Result', 'Result'),
              RESULTS: t('Results', 'Results'),
            },
          }}
          iconLeftName="location-dot"
          onOptionSelect={handleChangeLocation}
          size="medium"
          type="tertiary"
        >
          {t('Location', 'Location')}
        </ButtonDropdown>
      </div>
      {jobsLoading ? (
        <div className={css.loadingState}>
          <CircularLoader colorToken="colorFillPrimary" size="medium" />
        </div>
      ) : (
        <div className={css.jobsContainer}>
          {!filteredJob || filteredJob.length === 0 ? (
            <EmptyState
              classNames={{wrapper: css.panelHeight}}
              imageVariant="data"
              title={t('No_Jobs_Available', 'No Jobs Available')}
            />
          ) : (
            filteredJob.map((job) => {
              return isMobile(userAgent) ? (
                <MobileViewJobCard
                  job={job}
                  handleSelectJob={handleSelectJob}
                  selectJob={selectJob}
                  setJobDetailsOpen={setJobDetailsOpen}
                />
              ) : (
                <JobCard
                  job={job}
                  handleSelectJob={handleSelectJob}
                  selectJob={selectJob}
                  setJobDetailsOpen={setJobDetailsOpen}
                />
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export default JobListing;
